import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormControl, ReactiveFormsModule } from "@angular/forms";
import { DeactivateGuard } from "../deactivate.guard";
import { MhsService } from "../shared/service/mhs.service";
import { UserSessionService } from "../shared/service/user-session.service";
import { of, fromEvent, Subscription } from "rxjs";
import { Router, ActivatedRoute, NavigationEnd, NavigationExtras } from "@angular/router";
import { Location } from "@angular/common";
import { AppInsightsApiService } from "../shared/service/app-insights-api.service";
import { ManagedContentService } from "../shared/service/managed-content.service";
import { ContentType, CmHelp } from "../shared/model/cm/managed-content.model";
import { FormUtils } from "../shared/helper/form-utils";
import { AuthGuardService } from "../shared/service/auth-guard.service";
import { WindowRef } from "../shared/service/window.service";
import { AppInsightsLoggerService } from "../shared/service/app-insights-logger.service";
import { GoogleAnalyticsService } from "../shared/service/google-analytics.service";
import { ModalService } from "../shared/service/modal.service";
import { EEOI_APP_ROUTES } from '../shared/global-constants';
import { PagetitleService } from '../shared/service/pagetitle.service';
import { ViewportScroller } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { EmployeeInfoComponent } from "../employee-info/employee-info.component";
import { ProgressBarComponent } from "../progress-bar/progress-bar.component";
import { ApplicantInfoComponent } from "../applicant-info/applicant-info.component";
import { MedHistoryComponent } from "../med-history/med-history.component";
import { PhysicianInfoComponent } from "../physician-info/physician-info.component";
import { filter, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-mhs',
  standalone: true,
  imports: [ReactiveFormsModule, EmployeeInfoComponent, ProgressBarComponent, ApplicantInfoComponent, MedHistoryComponent, PhysicianInfoComponent],
  templateUrl: './mhs.component.html',
  styleUrls: ['./mhs.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MhsComponent implements OnInit, OnDestroy, DeactivateGuard {
  private cancelContent: CmHelp = this.cmService.getContentById(
    ContentType.HELP,
    "lose-data-warning"
  );
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);
  public cancelEmployeeInfo: { action: string; category: string; name: string; };
  private popStateSubscription: Subscription;
  private routerSubscription: Subscription;
  private previousStep: number = 1;

  // Use service properties for flags so they persist across component recreations
  // These getters/setters provide logging while using the service as the source of truth
  private get isNavigatingBack(): boolean {
    return this.mhsService.isNavigatingBack;
  }
  private set isNavigatingBack(value: boolean) {
    if (this.mhsService.isNavigatingBack !== value) {
    }
    this.mhsService.isNavigatingBack = value;
  }

  private get isHandlingPopstate(): boolean {
    return this.mhsService.isHandlingPopstate;
  }
  private set isHandlingPopstate(value: boolean) {
    this.mhsService.isHandlingPopstate = value;
  }

  private get isValidationFailed(): boolean {
    return this.mhsService.isValidationFailed;
  }
  private set isValidationFailed(value: boolean) {
    this.mhsService.isValidationFailed = value;
  }

  private get lastPushedUrl(): string {
    return this.mhsService.lastPushedUrl;
  }
  private set lastPushedUrl(value: string) {
    this.mhsService.lastPushedUrl = value;
  }

  constructor(
    private cmService: ManagedContentService,
    private userSessionService: UserSessionService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private mhsService: MhsService,
    private appInsightsApiService: AppInsightsApiService,
    private authGuardService: AuthGuardService,
    private windowService: WindowRef,
    private appInsightsLoggerService: AppInsightsLoggerService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private modalService: ModalService,
    private pagetitleService: PagetitleService,
    private scroller: ViewportScroller,
    private location: Location
  ) {
  }

  public isFormSubmitted: boolean;
  public employeeState;
  public timeInMilliseconds: string;
  public applicantType: string;
  public formNumber: string = this.userSessionService.data.formNumber;
  public isGenericDynamicForm: boolean = this.userSessionService.data.isGenericDynamicForm;

  mhs = new UntypedFormGroup({
    consented: new UntypedFormControl(this.userSessionService.data.consent),
    policyNumber: new UntypedFormControl(this.userSessionService.data.policy),
    groupName: new UntypedFormControl(),
    adminUnit: new UntypedFormControl(
      this.userSessionService.data.agencyLocation + ""
    ),
    formNumber: new UntypedFormControl(this.userSessionService.data.formNumber),
  });

  isValidatingSubmit: boolean;

  public currentStep = 1;
  public totalSteps = 3;
  completedSteps = [false, false, false];

  /**
   * True when the user arrived at this page via the Overview "View" button
   * on /review (URL ?mode=edit or history.state.editEmployee). In
   * that mode the MHS multi-step flow is collapsed down to the single
   * section the user is editing - so we hide the progress bar at the top
   * of the page because it no longer represents a meaningful step sequence.
   */
  public isEditFromReview: boolean = false;

  cancelCategory = [
    "employee-info",
    "applicant-info",
    "employee-info",
    "physician-info",
    // "auth-sign",
    // "auth-use-release-info",
  ];

  ngOnInit() {
    if (this.userSessionService.data.sessionEnded) {
      this.router.navigate(["/instructions"]);
    }

    // Detect edit-mode entry from the review Overview "View" button.
    // When true, the template hides the progress bar so the page reads as a
    // single-section view/edit, not a multi-step wizard.
    const isEditFromState = history.state
      && (history.state.editEmployee === true
          || history.state.editApplicant === true
          || history.state.editMedicalHistory === true
          || history.state.editPhysician === true);
    const isViewFromState = history.state
      && history.state.viewEmployee === true
      && history.state.fromMhsProgressBar !== true;
    const mode = this.activatedRoute.snapshot.queryParamMap.get('mode');
    const isEditFromQuery = mode === 'edit';
    const isViewFromQuery = mode === 'view';
    this.isEditFromReview = isEditFromState || isEditFromQuery || isViewFromState || isViewFromQuery;

    // Initialize navigation tracking to prevent auto-submit on page load
    this['_lastNavigationTime'] = Date.now();
    this['_userInitiatedSubmit'] = false;

    if (this.isGenericDynamicForm){
      this.totalSteps = 6;
      // Initialize or retrieve completed steps from session
      if (!this.userSessionService.data['mhsCompletedSteps']) {
        this.userSessionService.data['mhsCompletedSteps'] = [false, false, false, false];
      }
      this.completedSteps = this.userSessionService.data['mhsCompletedSteps'];
    }else {
      this.totalSteps = 5;
      // Initialize or retrieve completed steps from session
      if (!this.userSessionService.data['mhsCompletedSteps']) {
        this.userSessionService.data['mhsCompletedSteps'] = [false, false, false];
      }
      this.completedSteps = this.userSessionService.data['mhsCompletedSteps'];
    }

    // Determine current step based on route
    this.currentStep = this.getCurrentStepFromRoute();
    this.previousStep = this.currentStep;
    this.syncDependentStepCompletion();
    this.setPageTitle();

    // Initialize navigation stack with the current page if empty or if current page not in stack
    const currentUrl = this.router.url;
    if (this.mhsService.navigationStack.length === 0) {
      this.mhsService.navigationStack.push(currentUrl);
    } else if (this.mhsService.navigationStack[this.mhsService.navigationStack.length - 1] !== currentUrl) {
      // Current page is not at top of stack - add it (happens after forward navigation)
      if (!this.isNavigatingBack) {
        this.mhsService.navigationStack.push(currentUrl);
      }
    }

    // Listen to route changes to update currentStep when navigating via progress bar
    // Use distinctUntilChanged to prevent processing duplicate NavigationEnd events
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      distinctUntilChanged((prev: NavigationEnd, curr: NavigationEnd) => {
        const isDuplicate = prev.url === curr.url;
        if (!isDuplicate) {
        }
        return isDuplicate;
      })
    ).subscribe((event: NavigationEnd) => {
      const newUrl = event.url;
      const newStep = this.getCurrentStepFromRoute();
      // Track navigation time to prevent auto-submit on newly loaded pages
      this['_lastNavigationTime'] = Date.now();
      this['_userInitiatedSubmit'] = false;

      // Update current step
      if (newStep !== this.currentStep) {
        if (!this.isNavigatingBack) {
          this.previousStep = this.currentStep;
        }
        this.currentStep = newStep;
        this.syncDependentStepCompletion();
        this.setPageTitle();
      }

      // Add to navigation stack if NOT navigating back (forward navigation or progress bar click)
      if (!this.isNavigatingBack) {
        // Push the new page onto the stack if it's not already at the top
        if (this.mhsService.navigationStack[this.mhsService.navigationStack.length - 1] !== newUrl) {
          this.mhsService.navigationStack.push(newUrl);
        }
      }
    });

    this.isFormSubmitted = false;

    this.isValidatingSubmit = false;

    this.cancelEmployeeInfo = {
      action: "button_click",
      category: this.cancelCategory[this.currentStep - 1],
      name: "cancel",
    };

    if(this.appInsightsApiService){
      this.appInsightsApiService.endNavigationEvent(this.router.url);
    }

    // Handle browser back button 
      this.setupBrowserBackButton();
  }

  // ngAfterViewInit removed - empty lifecycle methods violate linting rules

  private setupBrowserBackButton(): void {
    // Push an initial state to ensure we can intercept the first back button click
    // This prevents the browser from navigating away from the application entirely
    const initialState = {
      navigationId: Date.now(),
      step: this.router.url,
      managedByMhs: true,
      isInitialState: true
    };
    history.pushState(initialState, '', window.location.pathname);

    // For Chrome: Also push a second state immediately to create a "buffer"
    // Chrome behaves differently and needs this extra entry to prevent navigation away
    setTimeout(() => {
      const bufferState = {
        navigationId: Date.now() + 1,
        step: this.router.url,
        managedByMhs: true,
        isBufferState: true
      };
      history.pushState(bufferState, '', window.location.pathname);
    }, 10);

    // Browser back button handler
    this.popStateSubscription = fromEvent(window, 'popstate').subscribe((event: PopStateEvent) => {
      // Get the URL from the location (where browser went) not Angular router (where Angular thinks we are)
      const browserUrl = window.location.pathname;
      const angularUrl = this.router.url;

      // Ignore if we're already processing a popstate event
      if (this.isHandlingPopstate) {
        return;
      }

      // Check if we're on an MHS page (check the Angular router URL, which is where we ARE)
      const mhsPages = ['employee-information', 'applicant-information', 'medical-history',
                        'physician-information', 'authorization', 'electronic-communication'];
      const isOnMhsPage = mhsPages.some(page => angularUrl.includes(page));

      if (isOnMhsPage) {
        // Find current step in the sequential order (based on where we WERE before back button)
        const currentStepIndex = this.mhsService.MHS_STEPS.findIndex(step => angularUrl.includes(step.replace('/', '')));

        // If we're on step 1 (employee-information), show the modal
        if (currentStepIndex === 0) {
          // Mark that we're handling popstate to prevent re-entry
          this.isHandlingPopstate = true;

          // Push forward to restore the current page using full browser path
          // This keeps us on the current page and prevents browser from navigating away
          // For Chrome: Push TWO states to ensure back button is always intercepted
          history.pushState(null, '', window.location.pathname);

          // Second push after a tiny delay to ensure Chrome registers both
          setTimeout(() => {
            history.pushState(null, '', window.location.pathname);
          }, 0);

          // Show the custom modal instead of browser's default "Leave site?" dialog
          this.modalService.showModal("prism-ui-modal-cancel-modal");

          // Reset the flag after a short delay
          setTimeout(() => {
            this.isHandlingPopstate = false;
          }, 100);

          return;
        }

        // If in edit-from-review mode on applicant-information, medical-history, or
        // physician-information, show the modal instead of navigating back to the
        // previous wizard step.
        const editFromReviewPages = ['applicant-information', 'medical-history', 'physician-information'];
        if (this.isEditFromReview && editFromReviewPages.some(page => angularUrl.includes(page))) {
          this.isHandlingPopstate = true;

          history.pushState(null, '', window.location.pathname);
          setTimeout(() => {
            history.pushState(null, '', window.location.pathname);
          }, 0);

          this.modalService.showModal("prism-ui-modal-cancel-modal");

          setTimeout(() => {
            this.isHandlingPopstate = false;
          }, 100);

          return;
        }

        // If we're on any other step, go to the PREVIOUS step in sequential order
        if (currentStepIndex > 0) {
          // Set flag BEFORE validation to prevent marking step as complete
          this.isNavigatingBack = true;

          // Validate current step before allowing navigation
          const isCurrentStepValid = this.validateCurrentStep();

          if (!isCurrentStepValid) {
            // Set flags to indicate we're handling a failed validation
            this.isHandlingPopstate = true;
            this.isValidationFailed = true;

            // Restore the current URL in the browser history (go forward to where we were)
            // IMPORTANT: Use the Angular router URL (where we actually ARE) not window.location.pathname
            // because the browser has already changed the URL but Angular hasn't navigated yet
            const currentStepPath = this.mhsService.MHS_STEPS[currentStepIndex];

            const currentState = {
              navigationId: Date.now(),
              step: currentStepPath,
              managedByMhs: true
            };

            // Build the full path including any base path
            // Extract base path from window.location.pathname by removing the Angular route
            const fullBrowserPath = window.location.pathname;
            const angularRoute = angularUrl.startsWith('/') ? angularUrl : '/' + angularUrl;
            const basePath = fullBrowserPath.includes(angularRoute)
              ? fullBrowserPath.substring(0, fullBrowserPath.indexOf(angularRoute))
              : '';
            const fullCurrentPath = basePath + currentStepPath;

            // Firefox requires TWO immediate pushes to properly prevent back navigation
            // Push the first state immediately
            history.pushState(currentState, '', fullCurrentPath);

            // Push the second state immediately (not async) for Firefox
            // This ensures the browser history has enough forward states to prevent going back
            history.pushState(currentState, '', fullCurrentPath);

            // CRITICAL: Prevent Angular from processing the location change
            // The browser has already changed to the previous URL, but we've pushed forward
            // Now we need to tell Angular to navigate to the current page to sync state
            const currentPageUrl = currentStepPath;

            // Keep the flag set during navigation to prevent canDeactivate interference
            this.router.navigate([currentPageUrl], { replaceUrl: true });

            // Wait for child component's onSubmit() to complete, then ensure scroll to first error
            setTimeout(() => {
              this.scrollToFirstError();
              // Reset flags after error display
              this.isHandlingPopstate = false;
              this.isNavigatingBack = false;
              this.isValidationFailed = false;
            }, 150);

            return;
          }

          const previousStep = this.mhsService.MHS_STEPS[currentStepIndex - 1];

          // Validation passed - clear the validation failed flag
          this.isValidationFailed = false;

          // Update the navigation stack to reflect where we're going
          // Pop everything after the previous step to keep stack clean
          const previousStepIndex = this.mhsService.navigationStack.indexOf(previousStep);
          if (previousStepIndex !== -1) {
            this.mhsService.navigationStack = this.mhsService.navigationStack.slice(0, previousStepIndex + 1);
          } else {
            // If previous step not in stack, add it
            this.mhsService.navigationStack.push(previousStep);
          }
          // Set popstate handling flag (isNavigatingBack already set above before validation)
          this.isHandlingPopstate = true;

          // Navigate to the previous step in sequential order
          // Don't use replaceUrl - let Angular's router handle navigation normally
          // The browser back button already popped the history, so we just need to navigate
          this.navigateToMhsRoute(previousStep).then((success) => {
            // Reset flags after a brief delay to allow navigation to fully settle
            // But keep the delay short (100ms) so Continue button works quickly
            setTimeout(() => {
              this.isNavigatingBack = false;
              this.isHandlingPopstate = false;
              this.isValidationFailed = false;
            }, 100);
          }).catch(err => {
            console.error('[POPSTATE] Navigation failed:', err);
            this.isNavigatingBack = false;
            this.isHandlingPopstate = false;
            this.isValidationFailed = false;
          });
        } // Close the if (currentStepIndex > 0) block

      }
    });
  }

  ngOnDestroy(): void {
    if (this.popStateSubscription) {
      this.popStateSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private getCurrentStepFromRoute(): number {
    const url = this.router.url;
    if (url.includes('employee-information')) return 1;
    if (url.includes('applicant-information')) return 2;
    if (url.includes('medical-history')) return 3;
    if (url.includes('physician-information')) return 4;
    // if (url.includes('authorization')) return 5;
    // if (url.includes('electronic-communication')) return 6;
    if (url.includes('mhs')) return 1; // Default to first step for /mhs route
    return 1;
  }

  private getRouteForStep(step: number): string {
    const routes = [
      EEOI_APP_ROUTES.EMPLOYEE_INFO,
      EEOI_APP_ROUTES.APPLICANT_INFO,
      EEOI_APP_ROUTES.MEDICAL_HISTORY,
      EEOI_APP_ROUTES.PHYSICIAN_INFO,
      // EEOI_APP_ROUTES.AUTHORIZATION,
      // EEOI_APP_ROUTES.ELECTRONIC_COMMUNICATION,
    ];
    return routes[step - 1] || routes[0];
  }

  private isDependentApplication(): boolean {
    const applicantType = this.userSessionService.data?.applicantType?.toLowerCase();
    return applicantType === 'spouse' || applicantType === 'child';
  }

  private syncDependentStepCompletion(): void {
    if (!this.isDependentApplication()) {
      return;
    }

    // In spouse/child journeys, employee info is pre-populated and should show
    // as completed once the user is on any subsequent step.
    if (this.currentStep > 1 && this.completedSteps.length > 0 && this.completedSteps[0] !== true) {
      this.completedSteps[0] = true;
      this.userSessionService.data['mhsCompletedSteps'] = this.completedSteps;
    }
  }

  private getNavigationExtrasForRoute(route: string): NavigationExtras {
    if (this.isDependentApplication() && route === EEOI_APP_ROUTES.EMPLOYEE_INFO) {
      return {
        state: {
          viewEmployee: true,
          fromMhsProgressBar: true
        }
      };
    }
    return {};
  }

  private navigateToMhsRoute(route: string, extras?: NavigationExtras): Promise<boolean> {
    const mergedExtras: NavigationExtras = {
      ...this.getNavigationExtrasForRoute(route),
      ...(extras || {})
    };
    return this.router.navigate([route], mergedExtras);
  }

  onScrollToTop(){
    this.scroller.setOffset([0, 200]);
    this.scroller.scrollToAnchor('topOfPage');
  }

  goToStep(step: number) {
    // Don't navigate to the current step
    if (step === this.currentStep) {
      return;
    }

    const isGoingBackward = step < this.currentStep;

    // Forward navigation remains gated by completion state.
    if (!isGoingBackward && !this.completedSteps[step - 1]) {
      return;
    }

    if (isGoingBackward) {
      // Backward progress-bar navigation should always be allowed so users can review prior sections.
      this.isNavigatingBack = true;
      const route = this.getRouteForStep(step);
      this.navigateToMhsRoute(route);

      setTimeout(() => {
        this.isNavigatingBack = false;
      }, 200);

      return;
    }

    // Forward navigation validates the current step before allowing route changes.
    const isCurrentStepValid = this.validateCurrentStep();

    // Only allow navigation if current step is valid
    if (isCurrentStepValid) {
      const route = this.getRouteForStep(step);
      this.navigateToMhsRoute(route);

    } else {
      // Current step has validation errors - block navigation and bring errors into view.
      setTimeout(() => {
        this.scrollToFirstError();
      }, 100);
    }
  }

  /**
   * Navigate to the previous step in the MHS flow
   * This is used by the "Back" button on each page (except employee-information which uses Cancel)
   */
  goToPreviousStep() {
    // Calculate previous step
    const previousStep = this.currentStep - 1;

    // Employee-information (step 1) should not have a back button (uses Cancel instead)
    if (previousStep < 1) {
      return;
    }

    // Back button should always allow returning to the prior step.
    // Do not gate this action on current-step validation.
    this.isNavigatingBack = true;

    const route = this.getRouteForStep(previousStep);
    this.router.navigate([route]).then(() => {
      this.onScrollToTop();
      setTimeout(() => {
        this.isNavigatingBack = false;
      }, 100);
    }).catch(() => {
      this.isNavigatingBack = false;
    });
  }


  validateCurrentStep(): boolean {
    let isValid = false;

    if (this.currentStep == 1) {
      if(this.mhsService){
        this.mhsService.resubmitEmployeeInfo(true);
      }
      isValid = this.mhs.controls["employee"]?.valid ?? false;
    } else if (this.currentStep == 2) {
      if(this.mhsService){
        this.mhsService.resubmitApplicantInfo(true);
      }
      isValid = this.mhs.controls["applicant"]?.valid ?? false;
    } else if (this.currentStep == 3) {
      if(this.mhsService){
        this.mhsService.resubmitMedicalHistory(true);
      }
      isValid = this.mhs.controls["medHistory"]?.valid ?? false;
    } else if (this.currentStep == 4) {
      if(this.mhsService){
        this.mhsService.resubmitPhysician(true);
      }
      isValid = this.mhs.controls["physician"]?.valid ?? false;
    // } else if (this.currentStep == 5) {
    //   if (this.isGenericDynamicForm){
    //     if(this.mhsService){
    //       this.mhsService.resubmitAuthSign(true);
    //     }
    //     isValid = this.mhs.controls["authSign"]?.valid ?? false;
    //   } else {
    //     // For non-generic forms, step 5 is the last step (auth-sign)
    //     if(this.mhsService){
    //       this.mhsService.resubmitAuthSign(true);
    //     }
    //     isValid = this.mhs.controls["authSign"]?.valid ?? false;
    //   }
    // } else if (this.currentStep == 6) {
    //   if(this.mhsService){
    //     this.mhsService.resubmitElectronicComm(true);
    //   }
    //   isValid = this.mhs.controls["electronicCommunication"]?.valid ?? false;
    } else {
      isValid = true; // Default to true if step is not recognized
    }

    // The child component's onSubmit() will handle scrolling to the first error
    // if validation fails, so we don't need to do it here

    return isValid;
  }

  private scrollToFirstError(): void {
    // Find the first invalid control in the current form
    const firstInvalidControl = document.querySelector('.ng-invalid:not(form)') as HTMLElement;

    if (firstInvalidControl) {
      const headerOffset = 170;
      const elementPosition = firstInvalidControl.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Wrapper method for user-initiated form submissions from child components
   * Sets the _userInitiatedSubmit flag before calling completedStep
   */
  handleUserSubmit(val: boolean): void {
    this['_userInitiatedSubmit'] = true;
    this['_userInitiatedStep'] = this.currentStep; // Track which step the user clicked Continue on
    this.completedStep(val);
  }

  completedStep(val: boolean): void {
    // Don't process completedStep during browser back navigation
    // EXCEPT: If we're on step 1, allow it because there's nowhere to go back from step 1
    // This fixes Windows issue where isNavigatingBack flag persists after rapid browser backs
    if (this.isNavigatingBack && this.currentStep !== 1) {
      return;
    }

    // Don't auto-navigate if we just arrived at this page (prevent auto-submit on page load with valid saved data)
    // Allow some time for the page to fully initialize before accepting completedStep calls
    // Increased threshold to 500ms - a real user cannot click Continue in less than 500ms after page load
    // Typical human reaction time is 200-300ms, plus time to move mouse/finger and click
    const timeSinceNav = Date.now() - (this['_lastNavigationTime'] || 0);
    const wasRecentNavigation = timeSinceNav < 500;
    const isCorrectStep = this['_userInitiatedStep'] === this.currentStep;

    // ALWAYS block auto-submit if page was just loaded (within 500ms)
    // This prevents child components from auto-emitting when they load with valid saved data
    // A real user cannot physically click Continue in less than 500ms after page navigation
    if (wasRecentNavigation) {
      return;
    }

    // Reset the flags immediately after checking to prevent them from affecting subsequent navigations
    const wasUserInitiated = this['_userInitiatedSubmit'];
    this['_userInitiatedSubmit'] = false;
    this['_userInitiatedStep'] = undefined;

    if (val) {
      this.completedSteps[this.currentStep - 1] = true;
      // Persist to session
      this.userSessionService.data['mhsCompletedSteps'] = this.completedSteps;

      if (this.currentStep < this.totalSteps) {
        // Always go to the next sequential step, regardless of completion status
        const nextStep = this.currentStep + 1;
        const route = this.getRouteForStep(nextStep);

        // Reset browser back flag when user clicks Continue
        if (this.isNavigatingBack) {
          this.isNavigatingBack = false;
        }

        // Track navigation time to prevent auto-submit on next page
        this['_lastNavigationTime'] = Date.now();

        this.navigateToMhsRoute(route).then(() => {
          // Push history state after successful forward navigation
          // This ensures browser back button works correctly

          // Build the full path including base path (e.g., /voy56204/ui/medical-history)
          const fullBrowserPath = window.location.pathname;
          const angularRoute = route.startsWith('/') ? route : '/' + route;
          const basePath = fullBrowserPath.includes(angularRoute)
            ? fullBrowserPath.substring(0, fullBrowserPath.indexOf(angularRoute))
            : '';
          const fullPath = basePath + angularRoute;

          const forwardState = {
            navigationId: Date.now(),
            step: route,
            managedByMhs: true,
            isForwardNav: true
          };
          history.pushState(forwardState, '', fullPath);

          this.onScrollToTop();
        });
        // Note: currentStep and page title will be updated automatically by NavigationEnd subscription
      }
    }
  }

  public setPageTitle(){
    if(this.currentStep == 1){
      this.pagetitleService.setPageTitle("Employee Information");
    }else if(this.currentStep == 2){
      this.pagetitleService.setPageTitle("Applicant Information");
    }else if(this.currentStep == 3){
      this.pagetitleService.setPageTitle("Medical History");
    }else if(this.currentStep == 4){
      this.pagetitleService.setPageTitle("Physician Information");
    }else if(this.currentStep == 5){
      this.pagetitleService.setPageTitle("Authorization and Electronic Signature");
    }else if(this.currentStep == 6){
      this.pagetitleService.setPageTitle("Electronic Communication");
    }
  }

  validatePreviousStep() {
    if (this.currentStep == 1) {
      if(this.mhsService){
        this.mhsService.resubmitEmployeeInfo(true);
      }
      return this.mhs.controls["employee"]?.valid ?? false;
    } else if (this.currentStep == 2) {
      if(this.mhsService){
        this.mhsService.resubmitApplicantInfo(true);
      }
      return this.mhs.controls["applicant"]?.valid ?? false;
    } else if (this.currentStep == 3) {
      if(this.mhsService){
        this.mhsService.resubmitMedicalHistory(true);
      }
      if (
        this.userSessionService.data.answeredYesToRequiredPhysicianQuestion &&
        !this.mhsService.physicianErrorThrown &&
        this.mhs.controls["physician"].get("hasPhysician").value === "none"
      ) {
        this.mhsService.updateThrowPhysicianError(true);
        // Don't modify currentStep here - just trigger physician validation
        // this.currentStep = 4; // REMOVED: This was causing progress bar to jump to step 4
      }
      return this.mhs.controls["medHistory"]?.valid ?? false;
    } else if (this.currentStep == 4) {
      if (
        this.userSessionService.data.answeredYesToRequiredPhysicianQuestion &&
        !this.mhsService.physicianErrorThrown &&
        this.mhs.controls["physician"].get("hasPhysician").value == "none"
      ) {
        this.mhsService.updateThrowPhysicianError(true);
        // Don't modify currentStep here - we're already on step 4
        // this.currentStep = 4; // REMOVED: This was redundant and could cause issues
      }
      if(this.mhsService){
        this.mhsService.resubmitPhysician(true);
      }
      return this.mhs.controls["physician"]?.valid ?? false;
    // } else if (this.currentStep == 5) {
    //   if (this.isGenericDynamicForm){
    //     if(this.mhsService){
    //       this.mhsService.resubmitAuthSign(true);
    //     }
    //     return this.mhs.controls["authSign"]?.valid ?? false;
    //   } else {
    //     return true;
    //   }
    // } else if (this.currentStep == 6) {
    //   if(this.mhsService){
    //     this.mhsService.resubmitElectronicCommunication(true);
    //   }
    //   return this.mhs.controls["electronicCommunication"]?.valid ?? false;
    }
    return false; // Default return value for safety
  }

  public submitApplication(val: boolean): void {
    if (val) {
      this.isFormSubmitted = true;
      this.router.navigate(['/review']);
      const confirmationRoute = EEOI_APP_ROUTES.AUTH_ELEC_SIGN;
      this.appInsightsApiService.startNavigationEvent(confirmationRoute.toString());
    }
  }

  public handleSubmitApplication(val: boolean): void {
    if (this.isGenericDynamicForm){
      this['_userInitiatedSubmit'] = true;
      this['_userInitiatedStep'] = this.currentStep; // Track which step the user clicked Continue on
      this.completedStep(val);
    } else {
      this.submitApplication(val);
    }
  }

  onCancel() {
    this.googleAnalyticsService.sendCustomEvent(this.cancelEmployeeInfo);
    this.isFormSubmitted = false;

    this.modalService.showModal("prism-ui-modal-cancel-modal"); // Use the service to show the modal
  }

  canDeactivate() {
    // A successful edit-from-review save sets a one-time bypass immediately
    // before navigating back to /review. Consume it here to avoid false
    // leave-anyway modals when router finalUrl is briefly unavailable.
    if (this.mhsService.allowReviewNavigationAfterSaveOnce) {
      this.mhsService.allowReviewNavigationAfterSaveOnce = false;
      return of(true);
    }

    // Allow navigation between MHS pages without showing cancel modal
    const currentUrl = this.router.url;
    const mhsPages = ['employee-information', 'applicant-information', 'medical-history', 'physician-information'];
    const isOnMhsPage = mhsPages.some(page => currentUrl.includes(page));

    // Check if we're trying to navigate to another MHS page or to confirmation
    const navigation = this.router.getCurrentNavigation();
    const routerState = navigation?.finalUrl?.toString();
    const isNavigatingToMhsPage = routerState && mhsPages.some(page => routerState.includes(page));
    const isNavigatingToConfirmation = routerState && routerState.includes('review');

    // ALWAYS allow backward navigation between MHS pages
    // If routerState is null, it's likely a browser back/forward button
    const isBrowserNavigation = !routerState || routerState === '';

    // Check if this is an unwanted navigation triggered by browser's popstate completing
    // This happens when we're on a lower step trying to go to a higher step while isNavigatingBack is true
    // IMPORTANT: Use this.currentStep (component state) instead of getCurrentStepFromRoute() (router state)
    // because the router URL may already be updated by the time canDeactivate runs
    const currentStepNumber = this.currentStep; // Use component state, not router state
    const targetStepNumber = this.getStepFromUrl(routerState || '');
    const isUnwantedForwardNav = this.isNavigatingBack && targetStepNumber > currentStepNumber;

    // CRITICAL: Detect browser back button navigation
    // For FORWARD nav (Continue button): currentStepNumber < targetStepNumber (e.g., 1 < 2)
    // For BACKWARD nav (browser back): currentStepNumber > targetStepNumber (e.g., 2 > 1)
    // Use component's currentStep, not router URL, because router may already be updated
    const isBackwardNavigation = currentStepNumber > targetStepNumber && targetStepNumber > 0;

    // Handle unwanted forward navigation during browser back handling
    if (isUnwantedForwardNav) {
      // NEVER return false - instead, allow the navigation but immediately navigate back
      // This prevents NavigationCancel from firing which breaks the popstate listener
      setTimeout(() => {
        this.router.navigate([currentUrl], { replaceUrl: true });
      }, 0);

      return of(true); // Allow the navigation (but we'll override it immediately)
    }

    // CRITICAL FIX: If this is a backward navigation from browser back button, validate before allowing
    // ALWAYS validate backward navigation, even if flags are already set (handles rapid back button presses)
    if (isBackwardNavigation) {
      // Check if we've already validated this navigation attempt and it FAILED
      // Use the explicit isValidationFailed flag set by popstate handler
      const isHandlingFailedValidation = this.isNavigatingBack && this.isHandlingPopstate && this.isValidationFailed;

      if (isHandlingFailedValidation) {
        return of(false);
      }

      // If both navigation flags are true but validation didn't fail, it's a successful validation navigating backward - allow it
      const isHandlingSuccessfulBackwardNav = this.isNavigatingBack && this.isHandlingPopstate && !this.isValidationFailed;
      if (isHandlingSuccessfulBackwardNav) {
        return of(true);
      }

      if (!this.isNavigatingBack || !this.isHandlingPopstate) {
        // We're going backwards: currentStepNumber > targetStepNumber
        // Validate the CURRENT step (where we are NOW) before allowing backward navigation
        const previousStepIndex = currentStepNumber - 1; // Convert to 0-based index
        let isCurrentStepValid = false;

      // Manually validate the current step based on its index
      if (previousStepIndex === 0) {
        if(this.mhsService){
          this.mhsService.resubmitEmployeeInfo(true);
        }
        isCurrentStepValid = this.mhs.controls["employee"]?.valid ?? false;
      } else if (previousStepIndex === 1) {
        if(this.mhsService){
          this.mhsService.resubmitApplicantInfo(true);
        }
        isCurrentStepValid = this.mhs.controls["applicant"]?.valid ?? false;
      } else if (previousStepIndex === 2) {
        if(this.mhsService){
          this.mhsService.resubmitMedicalHistory(true);
        }
        isCurrentStepValid = this.mhs.controls["medHistory"]?.valid ?? false;
      } else if (previousStepIndex === 3) {
        if(this.mhsService){
          this.mhsService.resubmitPhysician(true);
        }
        isCurrentStepValid = this.mhs.controls["physician"]?.valid ?? false;
      // } else if (previousStepIndex === 4) {
      //   if(this.mhsService){
      //     this.mhsService.resubmitAuthSign(true);
      //   }
      //   isCurrentStepValid = this.mhs.controls["authSign"]?.valid ?? false;
      // } else if (previousStepIndex === 5) {
      //   if(this.mhsService){
      //     this.mhsService.resubmitElectronicComm(true);
      //   }
      //   isCurrentStepValid = this.mhs.controls["electronicCommunication"]?.valid ?? false;
      }

      if (!isCurrentStepValid) {
        // Push history state to prevent browser from going back
        setTimeout(() => {
          const previousStepPath = this.mhsService.MHS_STEPS[previousStepIndex];
          const currentState = {
            navigationId: Date.now(),
            step: previousStepPath,
            managedByMhs: true
          };
          // Stay on the current page - use full browser path including base path
          const fullPath = window.location.pathname;
          history.pushState(currentState, '', fullPath);
          history.pushState(currentState, '', fullPath);

          // Scroll to first error
          this.scrollToFirstError();
        }, 0);

        // Return false to block the navigation
        return of(false);
      } else {
        // Set the flag so popstate handler knows validation already happened
        this.isNavigatingBack = true;
      }
      } // Close the if (!this.isNavigatingBack || !this.isHandlingPopstate) block
    }

    // Allow navigation if:
    // 1. Form is submitted (going to confirmation)
    // 2. Session has ended
    // 3. We're handling browser back button (isNavigatingBack flag) AND it's not unwanted forward nav
    // 4. Navigating between MHS pages (including browser back button)
    // 5. Browser navigation (browser back/forward buttons)
    // 6. We're handling popstate (validation failed, staying on current page)
    const shouldAllow = this.isFormSubmitted ||
                        this.userSessionService.data.sessionEnded ||
                        this.isNavigatingBack ||
                        this.isHandlingPopstate ||
                        (isOnMhsPage && (isNavigatingToMhsPage || isNavigatingToConfirmation || isBrowserNavigation));

    if (shouldAllow) {
      return of(true);
    } else {
      this.modalService.showModal("prism-ui-modal-cancel-modal");
      return this.userSessionService.data.sessionEnded;
    }
  }

  private getStepFromUrl(url: string): number {
    if (!url) return 0;
    if (url.includes('employee-information')) return 1;
    if (url.includes('applicant-information')) return 2;
    if (url.includes('medical-history')) return 3;
    if (url.includes('physician-information')) return 4;
    // if (url.includes('authorization')) return 5;
    // if (url.includes('electronic-communication')) return 6;
    return 0;
  }

  leaveAnyway() {
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(["/instructions"]);
  }

  onCancelModal(modalName: string) {
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log(
        "info",
        "Info log from Employee.component",
        `Modal with id ${modalName} not found or doesn't support close method.`
      );
    }
  }

  // use the window service to access the modal element
  // and close the modal when the user clicks outside of it
  onClick(event: Event) {
    const modal = this.windowService.nativeDocument.getElementById(
      "prism-ui-modal-cancel-modal"
    );

    if (modal && event.target === modal) {
      this.onCancelModal("prism-ui-modal-cancel-modal");
    }
  }
}
